import { useMemo, useState } from "react";
import { useLanguage } from "@/context/language-context/LanguageContext";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import triangle from "@/assets/images/language-selector/triangle-down.svg";
import spainFlag from "@/assets/images/language-selector/spainFlag.png";
import ukFlag from "@/assets/images/language-selector/ukFlag.png";
import { useLocation } from "react-router-dom";

const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();

  const translate = useTranslate();

  const location = useLocation();

  const [opendedSelector, setOpenedSelector] = useState(false);

  const languages = useMemo(() => {
    if (language === "es") {
      return {
        active: {
          label: "ESP",
          flag: spainFlag,
        },
        inactive: {
          code: "en",
          label: "ENG",
          flag: ukFlag,
        },
      };
    }
    return {
      active: {
        label: "ENG",
        flag: ukFlag,
      },
      inactive: {
        code: "es",
        label: "ESP",
        flag: spainFlag,
      },
    };
  }, [language]);

  const selectLanguage = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.TouchEvent<HTMLButtonElement>,
  ) => {
    const selected = event.currentTarget.value as "es" | "en";
    setLanguage(selected);
    setOpenedSelector(false);
  };

  const changeSelectorState = () => {
    const newState = !opendedSelector;
    setOpenedSelector(newState);
  };

  const breadcrumbs = useMemo(() => {
    const route = location.pathname;
    if (
      route.endsWith("carros-motor/checklist") ||
      route.endsWith("carros-motor/checklist/")
    ) {
      return <span>{translate("checklist")}</span>;
    }
    if (route.includes("carros-motor/checklist/")) {
      return (
        <>
          <span className="mr-2 text-ib-gray-main">
            {translate("checklist")}
          </span>
          <span className="mr-2">/</span>
          <span className="mr-2">{translate("engine")}</span>
        </>
      );
    }
    if (route.endsWith("carros-motor") || route.endsWith("carros-motor/")) {
      return <span>{translate("inspectionsList")}</span>;
    }
    return (
      <>
        <span className="mr-2 text-ib-gray-main">
          {translate("inspectionsList")}
        </span>
        <span className="mr-2">/</span>
        <span className="mr-2">{translate("inspectionStand")}</span>
      </>
    );
  }, [location, translate]);

  return (
    <div className="w-full h-[40px] bg-ib-gray-lightest border-b border-b-ib-gray-borders iberia flex flex-row justify-between items-center py-3 pl-6">
      <div className="text-[12px] leading-[16px] text-ib-gray-darkest">
        {breadcrumbs}
      </div>
      <div
        data-testid="language-selector-container"
        onBlur={() => setOpenedSelector(false)}
        className={`z-10 flex flex-col bg-ib-gray-lightest text-ib-main pl-4 h-fit border-b border-b-ib-gray-borders border-x ${
          opendedSelector
            ? " border-x-ib-gray-borders border-b self-baseline -translate-y-[11px]"
            : "border-x-ib-gray-lightest"
        }`}
      >
        <button
          type="button"
          className="flex flex-row gap-x-2 h-[40px] rounded-[0] items-center pr-[56px] "
          onClick={changeSelectorState}
        >
          <img
            src={languages.active.flag}
            width="15px"
            height="15px"
            className="mr-2"
          />
          <div className="min-w-[30px]">{languages.active.label}</div>
          <img src={triangle} />
        </button>
        {opendedSelector ? (
          <button
            type="button"
            className="flex flex-row gap-x-2 items-center h-[40px] rounded-[0] focus:border-none hover:border-none text-left"
            value={languages.inactive.code}
            onMouseDown={selectLanguage}
            onTouchStart={selectLanguage}
          >
            <img
              src={languages.inactive.flag}
              width="15px"
              height="15px"
              className="mr-2"
            />
            <div>{languages.inactive.label}</div>
          </button>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;
